// Search across projects, forgiving of typos.
//
// Everything is already in memory from the single content fetch, so this is
// plain filtering — no request, no Sanity search API, no loading state.
//
// Exact and partial matches win outright. Only when a term matches nothing
// does it fall back to edit distance, so "illistator" still finds Illustrator
// while precise queries stay predictable.

import { stripEmphasis } from "../../components/Emphasis";

const normalize = (value) =>
  stripEmphasis(value)
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "") // strip accents
    .replace(/[^a-z0-9\s]/g, " ")
    .replace(/\s+/g, " ")
    .trim();

// Fields are weighted by how people actually remember work they have seen.
// Someone coming back for a half-remembered project recalls its name or who it
// was for — not its scope. A hit in the description is usually incidental, so
// it ranks last without being excluded.
const FIELDS = [
  { key: "title", weight: 10 },
  { key: "client", weight: 8 },
  { key: "categories", weight: 5 },
  { key: "tools", weight: 4 },
  { key: "role", weight: 2 },
  { key: "scope", weight: 2 },
  { key: "description", weight: 1 },
];

// Each field normalised separately, so a term can be scored against the field
// it actually landed in rather than against everything mashed together.
const fieldsOf = (project) =>
  FIELDS.map(({ key, weight }) => {
    const raw = project[key];
    const text = normalize(Array.isArray(raw) ? raw.join(" ") : raw);
    return { weight, text, words: text ? text.split(" ") : [] };
  }).filter((field) => field.text);

// Damerau-Levenshtein, bounded. Plain Levenshtein scores a transposition as two
// edits, which puts figam out of reach of figma — and swapped letters are
// the most common typo of all. Counting a swap as one edit fixes that.
//
// Bounded because it abandons a row once every cell exceeds the limit, so a long
// description is not compared character by character in full.
function withinDistance(a, b, limit) {
  if (Math.abs(a.length - b.length) > limit) return false;

  let twoBack = null;
  let prev = Array.from({ length: b.length + 1 }, (_, i) => i);

  for (let i = 1; i <= a.length; i++) {
    const row = [i];
    let best = i;

    for (let j = 1; j <= b.length; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      let value = Math.min(row[j - 1] + 1, prev[j] + 1, prev[j - 1] + cost);

      // the swap: ab against ba costs one, not two
      if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) {
        value = Math.min(value, twoBack[j - 2] + 1);
      }

      row[j] = value;
      if (value < best) best = value;
    }

    if (best > limit) return false;
    twoBack = prev;
    prev = row;
  }

  return prev[b.length] <= limit;
}

// Longer words tolerate more slips; short ones barely any, or "web" would
// match "wed", "web" and "why" alike.
const allowedSlips = (term) => (term.length <= 4 ? 0 : term.length <= 7 ? 1 : 2);

// 0 means no match. Higher is a better match, so results can be ranked.
function scoreTerm(words, joined, term) {
  if (joined.includes(term)) {
    // A word starting with the term beats one merely containing it.
    return words.some((w) => w.startsWith(term)) ? 3 : 2;
  }

  const limit = allowedSlips(term);
  if (limit === 0) return 0;

  return words.some((w) => withinDistance(w, term, limit)) ? 1 : 0;
}

export function scoreProject(project, query) {
  const terms = normalize(query).split(" ").filter(Boolean);
  if (terms.length === 0) return 0;

  const fields = fieldsOf(project);

  let total = 0;
  for (const term of terms) {
    // A term counts once, at the best field it landed in — not once per field.
    // Otherwise a project mentioning a word in its role, scope and description
    // could outrank the project actually named after it.
    let best = 0;
    for (const { weight, text, words } of fields) {
      const tier = scoreTerm(words, text, term);
      if (tier > 0) best = Math.max(best, tier * weight);
    }

    if (best === 0) return 0; // every term has to land somewhere
    total += best;
  }

  return total;
}

export function searchProjects(projects, query) {
  if (!query.trim()) return projects;

  return projects
    .map((project) => ({ project, score: scoreProject(project, query) }))
    .filter(({ score }) => score > 0)
    .sort((a, b) => b.score - a.score)
    .map(({ project }) => project);
}
